import React from 'react';
import { submitRegister } from '../../actions/authActions';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import logoWhite from '../../images/logo-white.png';
import './SignupRedesign.css';

const GUEST_DRAFT_STORAGE_KEY = 'editpage:guest-homepage-draft';

const DEFAULT_SIGNUP_PREVIEW = {
  tag: '// generated from your prompt',
  headlineParts: ['A little place people ', 'come back to'],
  sub: 'Show what you are serving, take orders online, and let people order ahead.',
  cta: 'Order online',
};

function Copyright() {
  return (
    <p className="auth-footer">
      © {new Date().getFullYear()} Copyright: &nbsp;
      <a href="http://www.editpage.com" className="footer-link-cr">
        <strong>EditPage.com</strong>
      </a>
    </p>
  );
}

function Signup(props) {
  const [details, setDetails] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });

  const [emailTaken, setEmailTaken] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [socialError, setSocialError] = React.useState('');
  const [guestDraft, setGuestDraft] = React.useState(null);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const social = params.get('social');
    const token = params.get('token');
    const email = params.get('email');
    const message = params.get('message');

    if (social === 'success' && token && email) {
      localStorage.setItem('token', token);
      localStorage.setItem('email', email);
      window.location.assign('/beta');
      return;
    }

    if (social === 'error') {
      setSocialError(message || 'Social sign in failed. Please try again.');
    }

    if (params.get('from') === 'generator') {
      try {
        const raw = localStorage.getItem(GUEST_DRAFT_STORAGE_KEY);

        if (raw) {
          const parsed = JSON.parse(raw);

          if (parsed && parsed.scene) {
            setGuestDraft(parsed);
          }
        }
      } catch (error) {
        setGuestDraft(null);
      }
    }
  }, []);

  const preview = guestDraft && guestDraft.scene ? guestDraft.scene : DEFAULT_SIGNUP_PREVIEW;
  const previewHeadlineParts = Array.isArray(preview.headlineParts)
    ? preview.headlineParts
    : DEFAULT_SIGNUP_PREVIEW.headlineParts;
  const previewPrompt = guestDraft && guestDraft.prompt ? guestDraft.prompt.trim() : '';

  const validateEmail = email => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const checkIfEmailAlreadyExisits = email => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/users/checkEmail', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(email),
      mode: 'cors',
    })
      .then(response => {
        if (!response.ok) {
          console.log('Heres our error', response.statusText);
        }
        return response.json();
      })
      .then(data => {
        setEmailTaken(!data.data);
      })
      .catch(() => {
        setEmailTaken(false);
      });
  };

  const handleUserInput = e => {
    const { name, value } = e.target;
    setDetails(prevInputState => ({
      ...prevInputState,
      [name]: value,
    }));

    if (name === 'email') {
      setEmailTaken(false);
    }
  };

  const handleEmailBlur = () => {
    if (validateEmail(details.email)) {
      checkIfEmailAlreadyExisits({ email: details.email });
    }
  };

  const passwordScore = React.useMemo(() => {
    const value = details.password;
    let score = 0;

    if (value.length >= 6) score += 1;
    if (value.length >= 10) score += 1;
    if (/[A-Z]/.test(value) && /[0-9]/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value)) score += 1;

    return score;
  }, [details.password]);

  const isFirstNameValid = details.firstName.trim().length > 1;
  const isLastNameValid = details.lastName.trim().length > 1;
  const isEmailValid = validateEmail(details.email);
  const isPasswordValid = details.password.length >= 6;

  const canSubmit = isFirstNameValid && isLastNameValid && isEmailValid && isPasswordValid && !emailTaken;

  const register = e => {
    e.preventDefault();
    const loginCreds = {
      firstName: details.firstName,
      lastName: details.lastName,
      email: details.email,
      password: details.password,
    };

    if (canSubmit) {
      props.register(loginCreds);
    }
  };

  const startSocialSignup = provider => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    if (!backendUrl) {
      setSocialError('Social sign in is unavailable because backend URL is not configured.');
      return;
    }

    window.location.assign(`${backendUrl}/user/oauth/${provider}?redirect=signup`);
  };

  return (
    <div className="signup-redesign">
      <div className="signup-shell">
        <div className="signup-brand-panel">
          <a className="signup-logo" href="/" aria-label="EditPage home">
            <img src={logoWhite} alt="EditPage" />
          </a>

          <div>
            <div className="signup-brand-copy">
              <div className="signup-eyebrow">{guestDraft ? 'Your draft is ready' : 'Build for free'}</div>
              <h1>{guestDraft ? 'Keep building the site you just generated.' : 'Describe your site. Watch it build itself.'}</h1>
              <p>
                {guestDraft
                  ? 'Create your free account to keep editing this draft, claim your domain, and publish when you are ready.'
                  : 'No templates, no drag-and-drop - type what you want, watch it generate, then click anything to fine-tune it.'}
              </p>
            </div>

            <div className="signup-preview-card">
              <div className="signup-preview-bar">
                <div className="preview-dot" />
                <div className="preview-dot" />
                <div className="preview-dot" />
              </div>
              <div className="signup-preview-body">
                <div className="preview-tag mono">{preview.tag || DEFAULT_SIGNUP_PREVIEW.tag}</div>
                <h4>{previewHeadlineParts[0]}<span>{previewHeadlineParts[1]}</span></h4>
                <p>{preview.sub || DEFAULT_SIGNUP_PREVIEW.sub}</p>
                <span className="signup-preview-cta">{preview.cta || DEFAULT_SIGNUP_PREVIEW.cta}</span>
                {previewPrompt ? (
                  <p className="signup-preview-prompt">Prompt: {previewPrompt}</p>
                ) : null}
              </div>
            </div>
          </div>

          <div>
            <div className="signup-trust-list">
              <div className="signup-trust-item">
                <span className="signup-trust-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
                </span>
                Start on Free, then upgrade to Growth, Scale, or Enterprise
              </div>
              <div className="signup-trust-item">
                <span className="signup-trust-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
                </span>
                No credit card required to start
              </div>
              <div className="signup-trust-item">
                <span className="signup-trust-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
                </span>
                Unlimited bandwidth on every plan
              </div>
            </div>
            <div className="signup-panel-footer">© {new Date().getFullYear()} EditPage.com</div>
          </div>
        </div>

        <div className="signup-form-panel">
          <div className="signup-form-wrap">
            <div className="signup-form-head">
              <h2>Create your account</h2>
              <p>{guestDraft ? 'Sign up to save this draft and publish your website.' : 'Sign up to start building and publishing your website.'}</p>
            </div>

            <div className="signup-oauth-row">
              <button type="button" className="signup-oauth-btn" onClick={() => startSocialSignup('google')}>
                <svg viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.7-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" /><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1C3.4 21.3 7.4 24 12 24z" /><path fill="#FBBC05" d="M5.4 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.6.4-2.4V6.5H1.4C.5 8.2 0 10.1 0 12s.5 3.8 1.4 5.5l4-3.1z" /><path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.7 1.4 6.5l4 3.1c.9-2.8 3.5-4.8 6.6-4.8z" /></svg>
                Google
              </button>
              <button type="button" className="signup-oauth-btn" onClick={() => startSocialSignup('apple')}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-2.02 1.6-3.145 1.51-.11-1.14.44-2.31 1.14-3.09.75-.86 2.08-1.5 3.18-1.5zm3.62 17.14c-.28.65-.62 1.28-1.02 1.87-.55.82-1 1.39-1.35 1.71-.55.5-1.14.75-1.77.77-.46.01-1.01-.13-1.65-.42-.64-.29-1.23-.43-1.77-.43-.56 0-1.16.14-1.81.43-.65.29-1.17.44-1.58.45-.6.03-1.2-.23-1.79-.79-.38-.35-.86-.94-1.43-1.79-.61-.9-1.11-1.95-1.51-3.14-.43-1.28-.64-2.52-.64-3.72 0-1.37.3-2.56.89-3.55.47-.79 1.09-1.42 1.87-1.88.78-.46 1.62-.7 2.53-.71.49 0 1.15.16 1.98.47.83.31 1.36.47 1.6.47.18 0 .77-.18 1.77-.55.94-.34 1.74-.48 2.39-.43 1.77.14 3.1.84 3.99 2.1-1.58.96-2.36 2.3-2.35 4.03.01 1.35.5 2.47 1.47 3.36.44.42.93.74 1.48.97-.12.35-.25.68-.39 1z" /></svg>
                Apple
              </button>
            </div>

            {socialError ? <div className="signup-error signup-error-social">{socialError}</div> : null}

            <div className="signup-divider">or with email</div>

            <form onSubmit={register}>
              <div className="signup-field-row">
                <div className="signup-field">
                  <label htmlFor="firstName">First name</label>
                  <div className="signup-input-wrap">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    <input
                      autoComplete="given-name"
                      name="firstName"
                      required
                      onChange={handleUserInput}
                      id="firstName"
                      placeholder="First name"
                      type="text"
                      value={details.firstName}
                    />
                  </div>
                </div>

                <div className="signup-field">
                  <label htmlFor="lastName">Last name</label>
                  <div className="signup-input-wrap">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    <input
                      autoComplete="family-name"
                      name="lastName"
                      required
                      onChange={handleUserInput}
                      id="lastName"
                      placeholder="Last name"
                      type="text"
                      value={details.lastName}
                    />
                  </div>
                </div>
              </div>

              <div className="signup-field">
                <label htmlFor="email">Email address</label>
                <div className="signup-input-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 6l-10 7L2 6" /></svg>
                  <input
                    required
                    onChange={handleUserInput}
                    onBlur={handleEmailBlur}
                    id="email"
                    name="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    type="email"
                    value={details.email}
                  />
                </div>
                {emailTaken ? <div className="signup-error">You have already signed up with that email</div> : null}
              </div>

              <div className="signup-field">
                <label htmlFor="password">Password</label>
                <div className="signup-input-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                  <input
                    required
                    onChange={handleUserInput}
                    name="password"
                    placeholder="Create a password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="new-password"
                    value={details.password}
                  />
                  <button
                    type="button"
                    className="signup-pw-toggle"
                    onClick={() => setShowPassword(prev => !prev)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div className="signup-pw-hint">
                  <div className="signup-pw-bar" style={{ background: passwordScore >= 1 ? '#ff7a4d' : 'var(--signup-border)' }} />
                  <div className="signup-pw-bar" style={{ background: passwordScore >= 2 ? '#ff7a4d' : 'var(--signup-border)' }} />
                  <div className="signup-pw-bar" style={{ background: passwordScore >= 3 ? '#5fcdb8' : 'var(--signup-border)' }} />
                  <div className="signup-pw-bar" style={{ background: passwordScore >= 4 ? '#5fcdb8' : 'var(--signup-border)' }} />
                </div>
              </div>

              <button type="submit" className="signup-submit-btn" disabled={!canSubmit}>
                Create my account
              </button>
            </form>

            <div className="signup-fine-print">
              By signing up, you agree to EditPage&apos;s <a href="/terms">Terms of Use</a> and <a href="/privacy">Privacy Policy</a>.
            </div>

            <div className="signup-divider" />

            <div className="signup-signin-row">Already have an account? <a href="/login">Sign in</a></div>
          </div>
        </div>
      </div>
      <div className="signup-mobile-copyright">
        <Copyright />
      </div>
    </div>
  );
}

const mapStateToProps = state => {
  return {
    loggedIn: state.auth.loggedIn,
    email: state.auth.email,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    register: data => {
      dispatch(submitRegister(data));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Signup));
